import type { Ref } from "vue"; //

import { ref, computed, onMounted, onUnmounted, provide, inject } from "vue";

interface SidebarContextType {
  activeItem: Ref<null | string>;
  isExpanded: Ref<boolean>;
  isHovered: Ref<boolean>;
  isMobileOpen: Ref<boolean>;
  openSubmenu: Ref<null | string>;
  setActiveItem: (item: null | string) => void;
  setIsHovered: (isHovered: boolean) => void;
  toggleMobileSidebar: () => void;
  toggleSidebar: () => void;
  toggleSubmenu: (item: string) => void;
}

const SidebarSymbol = Symbol();

export function useSidebarProvider() {
  const isExpanded = ref(true);
  const isMobileOpen = ref(false);
  const isMobile = ref(false);
  const isHovered = ref(false);
  const activeItem = ref<null | string>(null);
  const openSubmenu = ref<null | string>(null);

  const handleResize = () => {
    const mobile = window.innerWidth < 768;
    isMobile.value = mobile;
    if (!mobile) {
      isMobileOpen.value = false;
    }
  };

  onMounted(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
  });

  onUnmounted(() => {
    window.removeEventListener("resize", handleResize);
  });

  const toggleSidebar = () => {
    if (isMobile.value) {
      isMobileOpen.value = !isMobileOpen.value;
    } else {
      isExpanded.value = !isExpanded.value;
    }
  };

  const toggleMobileSidebar = () => {
    isMobileOpen.value = !isMobileOpen.value;
  };

  const setIsHovered = (value: boolean) => {
    isHovered.value = value;
  };

  const setActiveItem = (item: null | string) => {
    activeItem.value = item;
  };

  const toggleSubmenu = (item: string) => {
    openSubmenu.value = openSubmenu.value === item ? null : item;
  };

  const context: SidebarContextType = {
    activeItem,
    isExpanded: computed(() => (isMobile.value ? false : isExpanded.value)),
    isHovered,
    isMobileOpen,
    openSubmenu,
    setActiveItem,
    setIsHovered,
    toggleMobileSidebar,
    toggleSidebar,
    toggleSubmenu,
  };

  provide(SidebarSymbol, context);

  return context;
}

export function useSidebar(): SidebarContextType {
  const context = inject<SidebarContextType>(SidebarSymbol);
  if (!context) {
    throw new Error(
      "useSidebar must be used within a component that has SidebarProvider as an ancestor"
    );
  }
  return context;
}
